import {
    FilterOp,
    useQueryClientsPatientsQuery,
    ClinicFieldsFragment,
} from '~graphql/generated/graphql'
import { useMemo } from 'react'
import { useDebounce } from './use-debounce'
import { PAGE_SIZES, PageSizeType } from '~config/constants'
import { usePageControls } from './use-page-controls'
import {
    eqBoolFilter,
    eqStringFilter,
    isNullFilter,
} from '~utils/query-helpers'

type UseSearchClientsProps = {
    clinic: Pick<ClinicFieldsFragment, 'id' | 'tmp_feature_flags'> | undefined
    query: string
    showInactive?: boolean
    showNonSystem?: boolean
    /**
     * For infinite scroll: `clientResults` holds every page loaded so far
     * instead of only the current page, and stays populated while the next
     * page loads. Call `setPage(page + 1)` to load more while `hasMore`.
     */
    shouldAccumulatePages?: boolean
}

export function useSearchClients({
    clinic,
    query,
    showInactive,
    showNonSystem,
    shouldAccumulatePages = false,
}: UseSearchClientsProps) {
    const debouncedQuery = useDebounce(query, 500)

    const { page, setPage, pageSize, setPageSize } = usePageControls(
        PAGE_SIZES[1],
        JSON.stringify([query, showInactive, showNonSystem]),
    )
    const queryPageSize = (page + 1) * pageSize

    // Check if clinic has location filtering enabled via temp feature flag
    const shouldFilterByLocation =
        clinic?.tmp_feature_flags?.only_use_this_location

    const filters = useMemo(
        () => [
            ...(shouldFilterByLocation && clinic?.id
                ? [eqStringFilter('client_clinic', clinic.id)]
                : []),
            {
                column: 'vector_search_field',
                filterOp: FilterOp.Fts,
                value: {
                    string: processSearchQuery(debouncedQuery),
                },
            },
            ...(showNonSystem ? [] : [isNullFilter('client_pms_id', true)]),
            ...(showInactive ? [] : [eqBoolFilter('client_is_active', true)]),
        ],
        [
            clinic?.id,
            debouncedQuery,
            shouldFilterByLocation,
            showInactive,
            showNonSystem,
        ],
    )

    const {
        data: currentClientData,
        previousData: previousClientData,
        loading,
        error,
    } = useQueryClientsPatientsQuery({
        variables: {
            clinicId: clinic?.id || '',
            where: filters,
            page: 0,
            pageSize: queryPageSize,
        },
        skip: !clinic?.id || !debouncedQuery.trim(),
    })

    // While a later page loads, `previousData` holds the same search's earlier
    // pages. Page 0 means a new search, so stale results are never shown.
    const clientData =
        currentClientData ??
        (shouldAccumulatePages && page > 0 ? previousClientData : undefined)

    const loadedClientResults =
        clientData?.queryClientsPatients?.results.filter(Boolean) || []

    const clientResults = shouldAccumulatePages
        ? loadedClientResults
        : loadedClientResults.slice(page * pageSize, (page + 1) * pageSize)

    const count = clientData?.queryClientsPatients?.count || 0
    // A short response means the server has nothing past it, even if `count`
    // (computed separately) disagrees.
    const hasMore =
        loadedClientResults.length >= queryPageSize &&
        loadedClientResults.length < count

    return {
        clientResults,
        loading,
        error,
        page,
        setPage,
        pageSize,
        setPageSize,
        count,
        hasMore,
    }
}

/**
 * Generates tsquery prefix-match variants for a digit string, accounting for
 * the stored token potentially having (or missing) a leading country code "1".
 */
function phoneSearchVariants(digits: string): string {
    if (digits.startsWith('1') && digits.length === 11) {
        return `(${digits}:* | ${digits.slice(1)}:*)`
    }
    return `(${digits}:* | 1${digits}:*)`
}

const MIN_PHONE_DIGIT_LENGTH = 4

export function processSearchQuery(query: string) {
    // Captures an optional leading country code "1" followed by a 10-digit phone pattern
    const phoneRegex = /1?[-.\s]?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/g
    const emailRegex = /\S+@\S+\.\S+/g

    let remainingQuery = query
    const terms: string[] = []

    // Extract and process phone numbers
    let phoneMatch
    while ((phoneMatch = phoneRegex.exec(query)) !== null) {
        const digitsOnly = phoneMatch[0].replace(/\D/g, '')
        terms.push(phoneSearchVariants(digitsOnly))

        remainingQuery = remainingQuery.replace(phoneMatch[0], '')
    }

    // Extract and process emails
    let emailMatch
    while ((emailMatch = emailRegex.exec(query)) !== null) {
        terms.push(emailMatch[0].replace(/[&|:*!()]/g, '') + ':*')

        remainingQuery = remainingQuery.replace(emailMatch[0], '')
    }

    // Process remaining terms
    const remainingTerms = remainingQuery
        .trim()
        .split(/\s+/)
        .filter(term => term.length > 0)
        .map(term => {
            const stripped = term.replace(/[^\w'-]/g, '')
            if (
                /^\d+$/.test(stripped) &&
                stripped.length >= MIN_PHONE_DIGIT_LENGTH
            ) {
                return phoneSearchVariants(stripped)
            }
            return stripped + ':*'
        })

    return [...terms, ...remainingTerms].join(' & ')
}
